// AirspaceDetail — slide-over detail card for an airspace polygon
// (SUA / TFR / AIRMET / SIGMET / GFA / CWA / Dispatch) the user
// tapped or long-pressed on.
//
// Source kinds:
//   sua      special-use airspace (Restricted, Prohibited, MOA,
//            Warning, Alert, CFA). Polygon properties from FAA AIXM.
//   tfr      temporary flight restriction (active NOTAM). Polygon
//            properties from the FAA TFR feed.
//   airmet   G-AIRMET (graphical airmen's met info). NWS payload.
//   sigmet   SIGMET. NWS payload.
//   dispatch NIFC interagency wildland-fire dispatch zone. Carries
//            dispatch name, GACC, contact phone, comments.
//
// Each shape carries different field names; we normalize them into a
// common set of "what does the pilot need to know" rows: name, kind,
// vertical limits (floor / ceiling), comms / freqs, schedule, and a
// short remarks paragraph if the source provides one.

import { formatZuluWithLocal } from '../lib/timeFormat';
import { getCachedOverlay } from '../map/overlayRuntime';
import DetailCard from '../components/DetailCard';

export type AirspaceKind = 'sua' | 'tfr' | 'airmet' | 'sigmet' | 'gfa' | 'cwa' | 'dispatch' | 'obstacle' | 'repeater' | 'classBCD' | 'airways';

export interface AirspaceDetailData {
  kind: AirspaceKind;
  /** Original GeoJSON properties (we read from this defensively). */
  properties: Record<string, unknown>;
}

interface Props {
  airspace: AirspaceDetailData;
  onClose: () => void;
}

interface NormalizedAirspace {
  title: string;
  subtitle: string;
  badgeColor: string; // tailwind class
  badgeLabel: string;
  rows: Array<[string, string]>;
  remarks?: string;
  /** Optional outbound link rendered as a small chip at the bottom of
   *  the detail card. Used by the TFR view to deep-link to the FAA
   *  TFR site for the full NOTAM text. */
  externalLink?: { label: string; href: string };
}

export default function AirspaceDetail({ airspace, onClose }: Props) {
  const data = normalize(airspace);

  return (
    <DetailCard
      headerDot={data.badgeColor}
      eyebrow={data.badgeLabel}
      title={data.title}
      subtitle={data.subtitle}
      onClose={onClose}
      rollup={data.rows}
      remarks={data.remarks}
      externalLink={data.externalLink}
    />
  );
}

// ---- normalization ----

function s(p: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = p[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function formatAlt(raw: string): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed || trimmed.toUpperCase() === 'GND' || trimmed.toUpperCase() === 'SFC') return 'SFC';
  // FL prefix already
  if (/^FL/i.test(trimmed)) return trimmed.toUpperCase();
  // Try a numeric parse — assume feet MSL when bare.
  const n = Number(trimmed);
  if (Number.isFinite(n)) return `${n.toLocaleString()} ft MSL`;
  return trimmed;
}

function normalize(a: AirspaceDetailData): NormalizedAirspace {
  switch (a.kind) {
    case 'sua':     return normalizeSua(a.properties);
    case 'tfr':     return normalizeTfr(a.properties);
    case 'airmet':  return normalizeAirmet(a.properties);
    case 'sigmet':  return normalizeSigmet(a.properties);
    case 'gfa':     return normalizeGfa(a.properties);
    case 'cwa':     return normalizeCwa(a.properties);
    case 'dispatch':return normalizeDispatch(a.properties);
    case 'obstacle':return normalizeObstacle(a.properties);
    case 'repeater':return normalizeRepeater(a.properties);
    case 'classBCD':return normalizeClassBCD(a.properties);
    case 'airways': return normalizeAirways(a.properties);
  }
}

/** NIFC aviation-frequency repeater site. Carries the site name, the
 *  parent dispatch center, the primary channel (Flt Following / Air
 *  Guard / Local) tied to this site, and a fold-out list of all
 *  channels the center serves — so a pilot tapping a mountain-top
 *  repeater knows exactly what to set in their radio for that area. */
function normalizeRepeater(p: Record<string, unknown>): NormalizedAirspace {
  const site     = s(p, 'site');
  const center   = s(p, 'center');
  const state    = s(p, 'state');
  const featureKind = s(p, 'feature_kind') || 'site';
  const geocoded = featureKind === 'center';
  const primary  = (p['primary_channel'] as Record<string, unknown> | null | undefined) ?? null;
  const channels = Array.isArray(p['center_channels']) ? p['center_channels'] as Array<Record<string, unknown>> : [];
  const toneNotes = s(p, 'tone_notes');

  const rows: Array<[string, string]> = [];
  if (geocoded) {
    rows.push(['Type', 'Dispatch center (approximate location)']);
  }
  if (center && !geocoded) rows.push(['Dispatch', center]);
  if (state)               rows.push(['Region',   state]);
  if (primary) {
    const rx   = num(primary, 'rx_mhz');
    const tx   = num(primary, 'tx_mhz');
    const tone = num(primary, 'tone_hz');
    const label = s(primary, 'label');
    if (rx != null) {
      const sameTx = tx == null || Math.abs(tx - rx) < 0.001;
      const freqStr = sameTx
        ? `${rx.toFixed(4)} MHz`
        : `RX ${rx.toFixed(4)} / TX ${(tx as number).toFixed(4)} MHz`;
      rows.push([label || 'Frequency', freqStr]);
      if (tone != null) rows.push(['Tone', `${tone.toFixed(1)} Hz`]);
    }
  }

  // List every other channel the parent center serves so a pilot can
  // see Flt Following / Air Guard / Local all in one place.
  const otherLines: string[] = [];
  for (const ch of channels) {
    const rx   = num(ch, 'rx_mhz');
    const tx   = num(ch, 'tx_mhz');
    const tone = num(ch, 'tone_hz');
    const label = s(ch, 'label');
    if (rx == null) continue;
    const sameTx = tx == null || Math.abs(tx - rx) < 0.001;
    const freqStr = sameTx
      ? `${rx.toFixed(4)}`
      : `RX ${rx.toFixed(4)} / TX ${(tx as number).toFixed(4)}`;
    const tonePart = tone != null ? `  T:${tone.toFixed(1)}` : '';
    otherLines.push(`${(label || 'Ch').padEnd(20)} ${freqStr}${tonePart} MHz`);
  }

  const remarksParts: string[] = [];
  if (otherLines.length) {
    remarksParts.push(
      `Channels available through ${center || 'this center'}:\n${otherLines.join('\n')}`
    );
  }
  if (toneNotes) {
    remarksParts.push(`Tone codes (where the channel uses **):\n${toneNotes}`);
  }
  remarksParts.push(
    'Source: 2013 NIFC Aviation Frequency Guide — verify currency before flight.'
  );

  return {
    title: site || 'Repeater site',
    subtitle: geocoded
      ? 'Dispatch center · approximate location'
      : (center ? `${center}` : 'NIFC aviation frequency'),
    badgeColor: 'bg-emerald-500',
    badgeLabel: geocoded ? 'DISPATCH CENTER · NIFC' : 'REPEATER · NIFC',
    rows,
    remarks: remarksParts.join('\n\n'),
  };
}

function normalizeClassBCD(p: Record<string, unknown>): NormalizedAirspace {
  const cls = s(p, 'CLASS') || '?';
  const name = s(p, 'NAME_TXT') || s(p, 'IDENT_TXT') || 'Class ' + cls;
  const upper = p.DISTVERTUPPER_VAL != null ? `${p.DISTVERTUPPER_VAL} ft MSL` : '—';
  const lower = p.DISTVERTLOWER_VAL != null ? `${p.DISTVERTLOWER_VAL} ft MSL` : 'SFC';
  const badgeColors: Record<string, string> = { B: 'bg-blue-500', C: 'bg-pink-500', D: 'bg-indigo-500' };
  return {
    title: `Class ${cls} Airspace`,
    subtitle: name,
    badgeLabel: `CLASS ${cls}`,
    badgeColor: badgeColors[cls] ?? 'bg-gray-500',
    rows: [
      ['Ceiling', upper],
      ['Floor', lower],
    ],
    remarks: s(p, 'REMARKS_TXT') || undefined,
  };
}

function normalizeAirways(p: Record<string, unknown>): NormalizedAirspace {
  const ident = s(p, 'IDENT') || '?';
  const cls = s(p, 'ROUTE_CLASS') || 'OTHER';
  const classLabels: Record<string, string> = {
    VICTOR: 'Victor Airway (Low Alt)',
    JET: 'Jet Route (High Alt)',
    RNAV_LO: 'T-Route (RNAV Low Alt)',
    RNAV_HI: 'Q-Route (RNAV High Alt)',
  };
  // The FAA AIS ATS_Route table uses MAA / MEA_E / MEA_W / MOCA rather
  // than UPPER_ALT / LOWER_ALT. The original normalizer read the latter
  // (which never exist), so airway taps always showed '—' for altitudes.
  // Fix shipped 2026-05-15.
  //
  // MAA_VAL  = Maximum Authorized Altitude (the upper limit)
  // MEA_E_VAL / MEA_W_VAL = Min Enroute Altitude (eastbound / westbound)
  //   FAA emits 999999 as 'not applicable' (one-way airways have only
  //   one MEA populated). Format both directions when valid; fall back
  //   to a single 'MEA' row when only one direction is set.
  // MOCA_VAL = Min Obstacle Clearance Altitude (advisory).
  // LEVEL_   = 'L' (low) / 'H' (high) / 'B' (both)
  const formatAltVal = (raw: unknown, uom: unknown): string => {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n === 999999) return '';
    const unit = typeof uom === 'string' && uom ? uom : 'FT';
    if (unit.toUpperCase().startsWith('FT')) {
      return n >= 18000 ? `FL${Math.round(n / 100)}` : `${n.toLocaleString()} ft MSL`;
    }
    return `${n.toLocaleString()} ${unit}`;
  };
  const maa  = formatAltVal(p.MAA_VAL,    p.MAA_UOM);
  const meaE = formatAltVal(p.MEA_E_VAL,  p.MEA_E_UOM);
  const meaW = formatAltVal(p.MEA_W_VAL,  p.MEA_W_UOM);
  const moca = formatAltVal(p.MOCA_VAL,   'FT');
  const lvl  = s(p, 'LEVEL_');
  const lvlLabel = lvl === 'H' ? 'High altitude'
                : lvl === 'L' ? 'Low altitude'
                : lvl === 'B' ? 'Low + High altitude'
                : '';

  const rows: Array<[string, string]> = [];
  rows.push(['Type', classLabels[cls] ?? cls]);
  if (lvlLabel) rows.push(['Level', lvlLabel]);
  if (maa)      rows.push(['MAA',   maa]);
  if (meaE && meaW) {
    rows.push(['MEA east',  meaE]);
    rows.push(['MEA west',  meaW]);
  } else if (meaE) {
    rows.push(['MEA', meaE]);
  } else if (meaW) {
    rows.push(['MEA', meaW]);
  }
  if (moca)     rows.push(['MOCA',  moca]);

  return {
    title: ident,
    subtitle: classLabels[cls] ?? 'Airway',
    badgeLabel: 'AIRWAY',
    badgeColor: 'bg-cyan-600',
    rows,
  };
}

function normalizeObstacle(p: Record<string, unknown>): NormalizedAirspace {
  const agl = num(p, 'agl');
  const msl = num(p, 'msl');
  const type = s(p, 'type');
  const lighting = s(p, 'lighting');
  const marking = s(p, 'marking');
  const qty = num(p, 'quantity');
  const state = s(p, 'state');
  const city = s(p, 'city');
  const id = s(p, 'id');

  const lightLabel = (() => {
    switch (lighting.toUpperCase()) {
      case 'R': return 'Red beacon';
      case 'M': return 'Medium-intensity white strobe';
      case 'H': return 'High-intensity white strobe';
      case 'F': return 'Floodlight';
      case 'D': return 'Dual (red + white)';
      case 'N': return 'Not lighted';
      case 'U': return 'Unknown';
      default: return lighting || '—';
    }
  })();

  const markLabel = (() => {
    switch (marking.toUpperCase()) {
      case 'M': return 'Marked';
      case 'N': return 'Not marked';
      case 'U': return 'Unknown';
      default: return marking || '—';
    }
  })();

  const rows: Array<[string, string]> = [];
  if (agl != null) rows.push(['Height AGL', `${agl.toLocaleString()} ft`]);
  if (msl != null) rows.push(['Height MSL', `${msl.toLocaleString()} ft`]);
  if (type)        rows.push(['Type',        type]);
  rows.push(['Lighting', lightLabel]);
  rows.push(['Marking',  markLabel]);
  if (qty && qty > 1) rows.push(['Quantity', `${qty} (group)`]);
  if (city || state)  rows.push(['Location', [city, state].filter(Boolean).join(', ')]);
  if (id)            rows.push(['FAA ID',   id]);

  // Color the badge by severity: tall (>=1000 ft) = red, otherwise amber.
  const tall = (agl ?? 0) >= 1000;
  return {
    title: type ? `${type.trim()} — ${(agl ?? 0).toLocaleString()} ft AGL` : `Obstacle — ${(agl ?? 0).toLocaleString()} ft AGL`,
    subtitle: lighting === 'N' ? 'Not lighted' : (lighting && lighting !== 'U' ? 'Lighted' : 'Lighting unknown'),
    badgeColor: tall ? 'bg-red-500' : 'bg-amber-500',
    badgeLabel: 'OBSTACLE · FAA DOF',
    rows,
  };
}

// Tiny number helper (s/n co-located with the kind normalizers below).
function num(p: Record<string, unknown>, k: string): number | null {
  const v = p[k];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number.parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function normalizeDispatch(p: Record<string, unknown>): NormalizedAirspace {
  // NIFC DMP_National_Dispatch_Boundaries_Public field set.
  const name     = s(p, 'DispName');
  const unitId   = s(p, 'DispUnitID');
  const location = s(p, 'DispLocation');
  const area     = s(p, 'DispArea');
  const phone    = s(p, 'ContactPhone');
  const gaccName = s(p, 'GACCName');
  const gaccAbbr = s(p, 'GACCAbbreviation');
  const gaccId   = s(p, 'GACCUnitID');
  const comments = s(p, 'Comments');

  const rows: Array<[string, string]> = [];
  if (unitId)   rows.push(['Unit ID', unitId]);
  if (location) rows.push(['Office', location]);
  if (gaccAbbr || gaccName) {
    rows.push(['GACC', gaccName ? `${gaccAbbr || ''} — ${gaccName}`.trim() : gaccAbbr]);
  }
  if (gaccId && gaccId !== gaccAbbr) rows.push(['GACC ID', gaccId]);
  if (phone) {
    // Format 10-digit US numbers as ###-###-#### for readability.
    const digits = phone.replace(/\D/g, '');
    const fmt = digits.length === 10
      ? `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`
      : phone;
    rows.push(['Contact', fmt]);
  }
  if (area) rows.push(['Area', area]);

  // Look up NIFC repeaters that fall geographically inside this zone
  // polygon (computed offline by the freq-guide parser via spatial
  // join). If the repeaters overlay has been opened at least once
  // this session, the cached FeatureCollection is available; otherwise
  // we silently skip — the user can opt the layer on and re-tap.
  const repeaterLines: string[] = name ? repeatersInZone(name) : [];
  let remarks = comments || '';
  if (repeaterLines.length) {
    remarks = (remarks ? `${remarks}\n\n` : '') +
      `Repeaters in this zone (NIFC 2013):\n${repeaterLines.join('\n')}`;
  }

  return {
    title: name || unitId || 'Dispatch zone',
    subtitle: gaccAbbr ? `${gaccAbbr} GACC` : 'Wildland fire dispatch',
    badgeColor: 'bg-lime-500',
    badgeLabel: 'DISPATCH · NIFC',
    rows,
    remarks: remarks || undefined,
  };
}

/** Return a flat list of "<site> — <label> <freq> T:<tone>" lines for
 *  every repeater whose `dispatch_zones` array includes the given
 *  DMP DispName. Reads from the cached repeaters overlay so this is
 *  a zero-network synchronous lookup. */
function repeatersInZone(dispName: string): string[] {
  const fc = getCachedOverlay('repeaters');
  if (!fc || !Array.isArray(fc.features)) return [];
  const out: string[] = [];
  for (const f of fc.features) {
    const props = f.properties as Record<string, unknown> | null;
    if (!props) continue;
    const zones = props['dispatch_zones'];
    if (!Array.isArray(zones) || !zones.includes(dispName)) continue;
    const site = String(props['site'] ?? 'Repeater').trim();
    const pc = props['primary_channel'] as Record<string, unknown> | null | undefined;
    if (pc) {
      const label = String(pc['label'] ?? '').trim();
      const rx = typeof pc['rx_mhz'] === 'number' ? pc['rx_mhz'] : null;
      const tx = typeof pc['tx_mhz'] === 'number' ? pc['tx_mhz'] : null;
      const tone = typeof pc['tone_hz'] === 'number' ? pc['tone_hz'] : null;
      const sameTx = tx == null || (rx != null && Math.abs(tx - rx) < 0.001);
      const freqStr = rx == null
        ? '?'
        : sameTx
          ? `${rx.toFixed(4)}`
          : `RX ${rx.toFixed(4)} / TX ${(tx as number).toFixed(4)}`;
      const tonePart = tone != null ? `  T:${tone.toFixed(1)}` : '';
      out.push(`• ${site.padEnd(20)} ${label || 'Ch'} ${freqStr}${tonePart} MHz`);
    } else {
      out.push(`• ${site}`);
    }
  }
  return out;
}

function normalizeSua(p: Record<string, unknown>): NormalizedAirspace {
  // SUA polygon properties from our /api/sua endpoint (FAA AIXM).
  const code = s(p, 'TYPE_CODE', 'TYPE');
  const name = s(p, 'NAME', 'name');
  const id   = s(p, 'IDENT', 'AREA_ID', 'AREA_NAME');
  const upper = s(p, 'UPPER_VAL', 'UPPER_LIMIT', 'UPPER');
  const lower = s(p, 'LOWER_VAL', 'LOWER_LIMIT', 'LOWER');
  const rows: Array<[string, string]> = [];
  if (id)         rows.push(['Designator', id]);
  if (upper)      rows.push(['Ceiling', formatAlt(upper)]);
  if (lower)      rows.push(['Floor',   formatAlt(lower)]);
  const sched = s(p, 'EFFECTIVE_DATE', 'SCHEDULE', 'TIME_OF_USE', 'HOURS');
  if (sched)      rows.push(['Schedule', sched]);
  const ctrl = s(p, 'CONTROLLING_AGENCY', 'CONTROL_AGENCY', 'AUTHORITY');
  if (ctrl)       rows.push(['Authority', ctrl]);
  const freq = s(p, 'FREQUENCY', 'FREQ', 'COMM_FREQ');
  if (freq)       rows.push(['Frequency', freq]);
  const remarks = s(p, 'REMARKS', 'REMARK');

  // SUA type-code → readable label + dot color.
  const typeMap: Record<string, [string, string]> = {
    P: ['Prohibited', 'bg-red-500'],
    R: ['Restricted', 'bg-orange-500'],
    W: ['Warning', 'bg-yellow-500'],
    A: ['Alert', 'bg-yellow-700'],
    M: ['MOA (Mil)', 'bg-purple-500'],
    C: ['CFA', 'bg-amber-500'],
    N: ['National Sec.', 'bg-rose-500'],
  };
  const [label, color] = typeMap[code.toUpperCase()] ?? ['Special Use', 'bg-amber-500'];
  return {
    title: name || id || 'Special use airspace',
    subtitle: label,
    badgeColor: color,
    badgeLabel: 'AIRSPACE · SUA',
    rows,
    remarks: remarks || undefined,
  };
}

/**
 * Normalize a TFR feature for display.
 *
 * Field map (FAA TFR WFS V_TFR_LOC, observed live 2026-05-11):
 *   NOTAM_KEY                   '6/0092-1-FDC-F'  — full key
 *   TITLE                       'Calexico, CA, Saturday, January 3, 2026 …'
 *   LEGAL                       'SECURITY' | 'AIR SHOWS/SPORTS' | 'VIP' | ...
 *   STATE                       2-letter state
 *   CNS_LOCATION_ID             ARTCC id (e.g. ZLA)
 *   LAST_MODIFICATION_DATETIME  'YYYYMMDDHHMM' UTC
 *
 * Plus our /api/tfr proxy enriches with these (from exportTfrList):
 *   tfr_type                    'VIP', 'SPACE OPERATIONS', etc.
 *   tfr_description             slightly different verbose blurb
 *   tfr_creation_date           'MM/DD/YYYY'
 *   tfr_detail_url              legacy per-NOTAM XML detail URL (often 404
 *                               on active fires — see api/tfr.ts notes)
 *   tfr_list_url                fallback link to FAA TFR3 SPA
 *
 * Older field aliases (TFR_SUBJECT, MAX_ALT, etc.) are kept for any
 * cached features the user still has in memory.
 */
function normalizeTfr(p: Record<string, unknown>): NormalizedAirspace {
  const id      = s(p, 'NOTAM_KEY', 'NOTAM_NUMBER', 'NOTAM_ID', 'TFR_NUM', 'notam_id');
  const title   = s(p, 'TITLE');
  const subj    = s(p, 'TFR_SUBJECT', 'NAME', 'TYPE');
  const tfrType = s(p, 'tfr_type', 'LEGAL', 'TYPE');
  const desc    = s(p, 'tfr_description');
  const state   = s(p, 'STATE', 'state');
  const artcc   = s(p, 'CNS_LOCATION_ID', 'facility');
  const created = s(p, 'tfr_creation_date');
  const lastMod = s(p, 'LAST_MODIFICATION_DATETIME');
  const upper   = s(p, 'MAX_ALT', 'UPPER_VAL', 'CEILING');
  const lower   = s(p, 'MIN_ALT', 'LOWER_VAL', 'FLOOR');
  const start   = s(p, 'TFR_START', 'EFF_BEG', 'EFFECTIVE');
  const end     = s(p, 'TFR_END', 'EFF_END', 'EXPIRES');
  const remarks = s(p, 'REMARKS', 'DESCRIPTION', 'TEXT');
  const detailUrl = s(p, 'tfr_detail_url');
  const listUrl   = s(p, 'tfr_list_url') || 'https://tfr.faa.gov/tfr3/';

  const rows: Array<[string, string]> = [];
  if (id)      rows.push(['NOTAM', id]);
  if (tfrType) rows.push(['Type', tfrType]);
  if (state || artcc) {
    rows.push(['Location', [state, artcc].filter(Boolean).join(' · ')]);
  }
  if (subj)    rows.push(['Subject', subj]);
  if (upper)   rows.push(['Ceiling', formatAlt(upper)]);
  if (lower)   rows.push(['Floor',   formatAlt(lower)]);
  if (start)   rows.push(['Begins',  start]);
  if (end)     rows.push(['Ends',    end]);
  if (created) rows.push(['Created', created]);
  if (lastMod) rows.push(['Updated', formatYyyymmddhhmm(lastMod)]);

  // Compose the remarks paragraph from whatever long-form text we have.
  // TITLE is usually the verbose dates blurb; tfr_description is
  // similar but sometimes shorter. We surface both when distinct so
  // the pilot has the most context possible until the FAA NOTAM
  // detail XMLs come back.
  const remarksParts: string[] = [];
  if (title) remarksParts.push(title);
  if (desc && desc !== title) remarksParts.push(desc);
  if (remarks) remarksParts.push(remarks);
  remarksParts.push(
    'For the full NOTAM text and graphic, follow the link below to the\n' +
    'FAA TFR site. Some active NOTAMs only have detail pages after the\n' +
    'effective date — use the list URL as a fallback.',
  );

  return {
    title: title || subj || id || 'Temporary Flight Restriction',
    subtitle: tfrType ? `Active TFR · ${tfrType}` : 'Active TFR',
    badgeColor: 'bg-red-500',
    badgeLabel: 'AIRSPACE · TFR',
    rows,
    remarks: remarksParts.join('\n\n'),
    externalLink: detailUrl
      ? { label: 'View full NOTAM on FAA TFR site', href: detailUrl }
      : { label: 'Open FAA TFR list', href: listUrl },
  };
}

/** FAA's LAST_MODIFICATION_DATETIME is 'YYYYMMDDHHMM' (UTC). Render as
 *  'YYYY-MM-DD HH:MM UTC'. Returns the input untouched when parsing
 *  fails. */
function formatYyyymmddhhmm(raw: string): string {
  if (!/^[0-9]{12}$/.test(raw)) return raw;
  return `${raw.slice(0,4)}-${raw.slice(4,6)}-${raw.slice(6,8)} ${raw.slice(8,10)}:${raw.slice(10,12)} UTC`;
}

function normalizeAirmet(p: Record<string, unknown>): NormalizedAirspace {
  const phenom = s(p, 'phenomenon', 'hazard', 'PHENOMENON');
  const sev = s(p, 'severity', 'SEVERITY');
  const upper = s(p, 'top', 'UPPER', 'TOP');
  const lower = s(p, 'base', 'LOWER', 'BASE');
  const valid = s(p, 'validTimeFrom', 'valid_from', 'EFFECTIVE');
  const exp = s(p, 'validTimeTo', 'valid_to', 'EXPIRES');
  const remarks = s(p, 'rawAirmet', 'raw', 'TEXT');
  const rows: Array<[string, string]> = [];
  if (phenom)  rows.push(['Hazard', phenom]);
  if (sev)     rows.push(['Severity', sev]);
  if (upper)   rows.push(['Top', formatAlt(upper)]);
  if (lower)   rows.push(['Base', formatAlt(lower)]);
  if (valid)   rows.push(['Valid from', friendlyTime(valid)]);
  if (exp)     rows.push(['Valid to',   friendlyTime(exp)]);
  return {
    title: phenom ? `${phenom} AIRMET` : 'AIRMET',
    subtitle: sev ? `Severity: ${sev}` : 'Graphical AIRMET',
    badgeColor: 'bg-blue-400',
    badgeLabel: 'AIRSPACE · AIRMET',
    rows,
    remarks: remarks || undefined,
  };
}

function normalizeGfa(p: Record<string, unknown>): NormalizedAirspace {
  // Properties produced by /api/wx/gfa: hazard, severity, top, base,
  // fzltop, fzlbase, level, dueTo, forecast (hours after issueTime),
  // validTime, issueTime, product, tag, topFt, baseFt.
  const hazard = s(p, 'hazard').toUpperCase();
  const sev = s(p, 'severity');
  const top = s(p, 'top');
  const base = s(p, 'base');
  const fzltop = s(p, 'fzltop');
  const fzlbase = s(p, 'fzlbase');
  const level = s(p, 'level');
  const validTime = s(p, 'validTime');
  const issueTime = s(p, 'issueTime');
  const forecast = s(p, 'forecast');
  const product = s(p, 'product');
  const tag = s(p, 'tag');
  const dueTo = s(p, 'dueTo');

  const rows: Array<[string, string]> = [];

  // Pretty hazard label first so the row is unambiguous.
  const labelMap: Record<string, [string, string]> = {
    'IFR':     ['IFR / Low Ceilings',     'bg-blue-400'],
    'MT_OBSC': ['Mountain Obscuration',   'bg-purple-500'],
    'ICE':     ['Icing',                  'bg-cyan-400'],
    'TURB-LO': ['Turbulence (Low)',       'bg-orange-500'],
    'TURB-HI': ['Turbulence (High)',      'bg-orange-500'],
    'LLWS':    ['Low-Level Wind Shear',   'bg-yellow-400'],
    'FZLVL':   ['Freezing Level',         'bg-cyan-500'],
    'SFC_WND': ['Surface Winds ≥ 30 KT',  'bg-yellow-500'],
  };
  const [label, color] = labelMap[hazard] ?? ['G-AIRMET hazard', 'bg-blue-400'];
  if (sev)         rows.push(['Severity', sev]);
  if (top)         rows.push(['Top', formatFlightLevel(top)]);
  if (base)        rows.push(['Base', formatFlightLevel(base)]);
  if (fzltop)      rows.push(['Freezing top', formatFlightLevel(fzltop)]);
  if (fzlbase)     rows.push(['Freezing base', formatFlightLevel(fzlbase)]);
  if (level)       rows.push(['Level', formatFlightLevel(level)]);
  if (forecast)    rows.push(['Forecast', `+${forecast}h after issuance`]);
  if (validTime)   rows.push(['Valid', friendlyTime(validTime)]);
  if (issueTime)   rows.push(['Issued', friendlyTime(issueTime)]);
  if (product || tag) rows.push(['G-AIRMET', [product, tag].filter(Boolean).join(' ')]);

  return {
    title: label,
    subtitle: sev ? `${sev} · G-AIRMET` : 'G-AIRMET',
    badgeColor: color,
    badgeLabel: 'AIRSPACE · GFA',
    rows,
    remarks: dueTo || undefined,
  };
}

function normalizeCwa(p: Record<string, unknown>): NormalizedAirspace {
  // Properties from /api/wx/cwa: cwsu, name, seriesId, validTimeFrom,
  // validTimeTo, hazard, qualifier, cwaText.
  const cwsu   = s(p, 'cwsu');
  const name   = s(p, 'name');
  const series = s(p, 'seriesId');
  const hazard = s(p, 'hazard').toUpperCase();
  const qual   = s(p, 'qualifier');
  const validFrom = s(p, 'validTimeFrom');
  const validTo   = s(p, 'validTimeTo');
  const cwaText   = s(p, 'cwaText');

  const labelMap: Record<string, [string, string]> = {
    'IFR':        ['IFR / Low Ceilings', 'bg-blue-400'],
    'TURB':       ['Turbulence',         'bg-orange-500'],
    'ICE':        ['Icing',              'bg-cyan-400'],
    'CONVECTIVE': ['Convective',         'bg-red-500'],
    'MT_OBSC':    ['Mountain Obscuration','bg-purple-500'],
    'OTHER':      ['Advisory',           'bg-pink-500'],
  };
  const [label, color] = labelMap[hazard] ?? ['Advisory', 'bg-pink-500'];

  const rows: Array<[string, string]> = [];
  if (cwsu)       rows.push(['CWSU', name ? `${cwsu} — ${name}` : cwsu]);
  if (series)     rows.push(['Series', series]);
  if (qual)       rows.push(['Qualifier', qual]);
  if (validFrom)  rows.push(['Valid from', friendlyTime(validFrom)]);
  if (validTo)    rows.push(['Valid to',   friendlyTime(validTo)]);

  return {
    title: label,
    subtitle: cwsu ? `CWA · ${cwsu}` : 'Center Weather Advisory',
    badgeColor: color,
    badgeLabel: 'AIRSPACE · CWA',
    rows,
    remarks: cwaText || undefined,
  };
}

/** Format a G-AIRMET top/base value ("380" → "FL380", "SFC" → "SFC",
 *  "FZL" → "Freezing Level"). */
function formatFlightLevel(s: string): string {
  const t = s.trim().toUpperCase();
  if (!t) return '';
  if (t === 'SFC') return 'Surface';
  if (t === 'FZL') return 'Freezing level';
  if (/^\d{1,3}$/.test(t)) return `FL${t.padStart(3, '0')}`;
  return t;
}

/** Tighten an ISO time to a friendlier 'YYYY-MM-DD HH:MMZ' display. */
// Pretty-print an aviation timestamp (ISO / epoch ms / epoch s) as
// Zulu + the user's local time. Wraps the shared formatZuluWithLocal
// helper so AIRMET / SIGMET / CWA / GFA / TFR rows show consistent
// 'DDHHMMZ (h:mm a PST)' formatting. Boss flag 2026-05-10.
function friendlyTime(v: string | number | null | undefined): string {
  return formatZuluWithLocal(v);
}

function normalizeSigmet(p: Record<string, unknown>): NormalizedAirspace {
  const phenom = s(p, 'hazard', 'phenomenon', 'PHENOMENON');
  const sev = s(p, 'severity', 'SEVERITY');
  const upper = s(p, 'top', 'UPPER');
  const lower = s(p, 'base', 'LOWER');
  const valid = s(p, 'validTimeFrom', 'EFFECTIVE');
  const exp = s(p, 'validTimeTo', 'EXPIRES');
  const remarks = s(p, 'rawSigmet', 'raw', 'TEXT');
  const rows: Array<[string, string]> = [];
  if (phenom)  rows.push(['Hazard', phenom]);
  if (sev)     rows.push(['Severity', sev]);
  if (upper)   rows.push(['Top', formatAlt(upper)]);
  if (lower)   rows.push(['Base', formatAlt(lower)]);
  if (valid)   rows.push(['Valid from', friendlyTime(valid)]);
  if (exp)     rows.push(['Valid to',   friendlyTime(exp)]);
  return {
    title: phenom ? `${phenom} SIGMET` : 'SIGMET',
    subtitle: 'Significant met info',
    badgeColor: 'bg-red-500',
    badgeLabel: 'AIRSPACE · SIGMET',
    rows,
    remarks: remarks || undefined,
  };
}
